import {
  CloseOutlined,
  DeleteOutlined,
  LoadingOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import {
  Button,
  Col,
  Form,
  Modal,
  Row,
  Typography,
  Upload,
} from "antd";
import {
  buildInitialValues,
  buildValidationSchema,
  FormDrawer,
} from "Components";
import Arrow from "assets/svg/arrow.svg";
import ivrPending from "assets/svg/ivrPending.svg";
import vendorPlaceholder from "assets/svg/vendorPlaceholder.png";
import axios from "axios";
import { useTranslation } from "contexts/TranslationContext";
import { Formik } from "formik";
import useAuthHook from "hooks/useAuthHook";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  useCreateNewMurabahaAppMutation,
  useGetVendorByIdQuery,
  useUpdateAppDetailsV2Mutation,
} from "services/auth";
import BackButton from "shared/components/BackButton/BackButton";
import env from "shared/constants/env";
import routes, { appRoute } from "shared/constants/routes";
import { ensureDynamicAmountTranslations } from "utilities/dynamicAmountValidation";
import * as Yup from "yup";
import SalaryEligibilityGate from "components/SalaryEligibility/SalaryEligibilityGate";

const { Title } = Typography;

// ── Hardcoded Temenos refs per team-lead mapping (EDU category) ──────────────
// All schools use the same productRef/productGroupRef/productName regardless of vendor.
// Only productSubType comes from vendor.SUBPRODUCT_NAME.
// See docs/DYNAMIC_VENDORS_PLAN.md §0 for the full mapping table.
const PRODUCT_REF = "SMB.TA.PERSONAL.FINANCE.EDU";
const PRODUCT_GROUP_REF = "SMB.TA.PERSONAL.FINANCE.EDU";
const PRODUCT_NAME = "APL-EDU";

const SchoolProductsV2 = () => {
  const { translate, language } = useTranslation();
  const { id } = useParams(); // numeric vendor.ID (matches /v2/vendors/:id)
  const navigate = useNavigate();
  const { getUser } = useAuthHook();
  const formikRef = useRef(null);

  // V2 mutations only — V1 path is deprecated and not mirrored here.
  const [createNewMurabahaApp, createNewMurabahaAppStatus] =
    useCreateNewMurabahaAppMutation();
  const [, updateAppDetailsStatusV2] = useUpdateAppDetailsV2Mutation();

  // Vendor fetch — gates the entire form. Redirect back to listing on 404.
  const {
    data: vendor,
    isLoading: vendorLoading,
    isError: vendorError,
  } = useGetVendorByIdQuery(id, { skip: !id });

  useEffect(() => {
    if (!vendorLoading && (vendorError || vendor === null)) {
      toast.error(
        translate("SIGN_IN.SOMETHING_WENT_WRONG") || "Vendor not found"
      );
      navigate(appRoute(routes.SCHOOLS_V2 + "/list"), { replace: true });
    }
  }, [vendor, vendorLoading, vendorError, navigate, translate]);

  // Doc selection state (V2 flow — files chosen before app create, uploaded after).
  const [selectedBankStatement, setSelectedBankStatement] = useState(null);
  const [selectedPaySlip, setSelectedPaySlip] = useState(null);
  const [selectedOtherDocuments, setSelectedOtherDocuments] = useState(null);

  const [bankStatementUploading, setBankStatementUploading] = useState(false);
  const [paySlipUploading, setPaySlipUploading] = useState(false);
  const [otherDocumentsUploading, setOtherDocumentsUploading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const [isCreating, setIsCreating] = useState(false);
  const [isIvrModalVisible, setIsIvrModalVisible] = useState(false);
  const [ivrApplicationId, setIvrApplicationId] = useState(null);

  const isLoading =
    createNewMurabahaAppStatus.isLoading || updateAppDetailsStatusV2.isLoading;
  const isAnyFileUploading =
    bankStatementUploading || paySlipUploading || otherDocumentsUploading;

  // ── Dynamic form schema (rebuilt when vendor loads) ────────────────────────
  // Yup messages use generic AMOUNT keys; the actual min/max range is surfaced
  // in the requested-amount notice card below so the user still sees the limits.
  const tenureOptions = useMemo(
    () =>
      (vendor?.TENORS || []).map((t) => ({
        value: String(t),
        label: String(t),
      })),
    [vendor?.TENORS]
  );

  const schemaFields = useMemo(() => {
    const min = Number(vendor?.AMOUNT_MIN ?? 0);
    const max = Number(vendor?.AMOUNT_MAX ?? Number.MAX_SAFE_INTEGER);
    // Augment the locale dicts with this vendor's specific min/max keys so
    // FormDrawer's translate("CREATE_REQUEST." + meta.error) resolves to
    // "Must be at least <min>" / "Cannot exceed <max>" rather than a stale
    // hardcoded value.
    const { minKey, maxKey } = ensureDynamicAmountTranslations(min, max);

    return [
      {
        type: "number",
        key: "request_amount",
        label: "Requested_loan_amount_",
        span: 24,
        required: true,
        pattern: "^[0-9]*$",
        validation: Yup.number()
          .required("CANNOT_BE_EMPTY")
          .min(min, minKey)
          .max(max, maxKey),
      },
      {
        type: "select",
        key: "tenure",
        label: "Tenure_",
        required: true,
        options: tenureOptions,
        span: 12,
        validation: Yup.string().required("CANNOT_BE_EMPTY"),
      },
      {
        type: "number",
        key: "salesRef",
        label: "ReferenceID",
        span: 12,
        required: false,
        max: 10,
        min: 8,
        maxLength: 10,
        minLength: 8,
        validation: Yup.string().matches(
          /^\d{8,10}$/,
          "MUST_BE_BETWEEN_8_AND_10_DIGITS"
        ),
      },
    ];
  }, [vendor?.AMOUNT_MIN, vendor?.AMOUNT_MAX, tenureOptions]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const isArabic = language?.code === "ar";
  // Fall back once if the vendor logo is broken/expired. Kept in state rather
  // than mutating img.src, so it survives re-renders and a placeholder that
  // also fails can't retrigger onError forever.
  const [logoFailed, setLogoFailed] = useState(false);
  const vendorName =
    (isArabic ? vendor?.VENDOR_NAME_AR : vendor?.VENDOR_NAME_EN) ||
    vendor?.VENDOR_NAME_EN ||
    vendor?.VENDOR_NAME_AR ||
    "-";
  const vendorLongDesc =
    (isArabic ? vendor?.LONG_DESC_AR : vendor?.LONG_DESC_EN) ||
    vendor?.LONG_DESC_EN ||
    vendor?.LONG_DESC_AR ||
    "";
  const vendorTerms =
    (isArabic ? vendor?.TERMS_AR : vendor?.TERMS_EN) ||
    vendor?.TERMS_EN ||
    [];

  // ── File validation (copied verbatim from legacy SchoolProducts) ───────────
  const isDuplicateFileName = (fileName) => {
    const existingFiles = [
      selectedBankStatement,
      selectedPaySlip,
      selectedOtherDocuments,
    ].filter((file) => file !== null);
    return existingFiles.some((file) => file.name === fileName);
  };

  const validateFile = (file) => {
    if (isDuplicateFileName(file.name)) {
      toast.error(
        `${translate("SETTINGS.ERROR_FILE_DUPLICATE")} / لقد تم تحديد هذا الملف بالفعل`
      );
      return false;
    }
    const isValidType =
      file.type === "image/jpeg" ||
      file.type === "image/png" ||
      file.type === "image/jpg" ||
      file.type === "application/pdf";
    if (!isValidType) return false;
    const isUnder3MB = file.size / 1024 / 1024 < 3;
    if (!isUnder3MB) return false;
    const fileName = file.name;
    const multipleExtensionsPattern = /(\.[^.\s]{2,4}){2,}$/;
    if (multipleExtensionsPattern.test(fileName)) return false;
    const invalidFileNamePattern = /^[^.]+\.[^.]+\..+\.[^.]+$/;
    if (invalidFileNamePattern.test(fileName)) return false;
    if (fileName.toLowerCase().includes("contract")) return false;
    return true;
  };

  // ── V2 doc upload (mirrors legacy SchoolProducts.uploadV2Documents) ────────
  // Schools use docType=OTHERS for all uploaded files (matches the legacy behavior).
  const uploadSingleDocument = async (file, documentType, appId) => {
    const formData = new FormData();
    formData.append("document", file.originFileObj || file);
    formData.append("appId", appId);
    formData.append("docType", documentType);

    const data = JSON.parse(localStorage.getItem("@@TAMWEEL_USER"));
    try {
      await axios.post(`${env.API_ENDPOINT_V2}app/upload-document`, formData, {
        headers: {
          Authorization: `Bearer ${data.token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      return true;
    } catch (err) {
      console.error(`> Failed to upload document: ${file.name}`, err);
      throw err;
    }
  };

  const uploadV2Documents = async (appId) => {
    const filesToUpload = [
      selectedBankStatement,
      selectedPaySlip,
      selectedOtherDocuments,
    ].filter(Boolean);

    if (filesToUpload.length === 0) return;

    setIsUploading(true);
    try {
      await Promise.all(
        filesToUpload.map((file) => uploadSingleDocument(file, "OTHERS", appId))
      );
    } catch (error) {
      console.error("> Error uploading school documents:", error);
      toast.error("Some documents failed to upload");
    } finally {
      setIsUploading(false);
    }
  };

  // ── Create application ─────────────────────────────────────────────────────
  const schoolAppCreateV2 = async (values) => {
    if (!vendor) {
      toast.error("Vendor data not loaded yet. Please try again.");
      return;
    }
    try {
      setIsCreating(true);
      const user = getUser();
      if (!user) {
        setIsCreating(false);
        return;
      }

      const res = await createNewMurabahaApp({
        productRef: PRODUCT_REF,
        productGroupRef: PRODUCT_GROUP_REF,
        productName: PRODUCT_NAME,
        productSubType: vendor.SUBPRODUCT_NAME, // "EDUCATIONFEES"
        idType: String(user?.USER_ID)[0] === "1" ? "NATIONAL.ID" : "IQAMA.ID",
        idNumber: String(user?.USER_ID),
        userWebId: Number(user?.IDB_USR_ID),
        source: "Web",
        details: {
          request_amount: values.request_amount,
          tenure: values.tenure,
          salesRef: values.salesRef || null,
          dealerId: vendor.VENDOR_ID, // ← "VND-001" style string, per team-lead spec
        },
      }).unwrap();

      if (res?.data?.applicationId) {
        const appId = res.data.applicationId;
        await uploadV2Documents(appId);
        setIvrApplicationId(appId);
        setIsIvrModalVisible(true);
        setIsCreating(false);
      } else {
        toast.error("Failed to create application");
        setIsCreating(false);
      }
    } catch (ex) {
      console.error("Error in create school application:", ex);
      const errorCode = ex?.data?.error?.code;
      if (
        errorCode === "ACTIVE_APPLICATION" ||
        errorCode === "APPLICATION_EXISTS"
      ) {
        toast.error(translate("ACTIVE_APPLICATION"));
      } else {
        toast.error(
          ex?.data?.error?.message || "Error in creating application"
        );
      }
      setIsCreating(false);
    }
  };

  // ── V2 document selection sub-component (mirrors legacy) ───────────────────
  const V2DocumentSelectionComponent = ({ title, file, onSelect, onRemove }) => {
    const handleFileSelect = (info) => {
      const selectedFile = info.file;
      if (validateFile(selectedFile)) {
        onSelect(selectedFile);
      }
    };

    const uploadProps = {
      name: "file",
      multiple: false,
      accept: "application/pdf",
      beforeUpload: () => false, // prevent Antd auto-upload
      onChange: handleFileSelect,
      showUploadList: false,
    };

    return (
      <div className="border border-gray-200 border-dashed rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center">
              <UploadOutlined className="text-teal-600" />
            </div>
            <div>
              <p className="font-medium text-gray-700">
                {translate(`CREATE_REQUEST.${title}`)}
              </p>
              {file && (
                <p className="text-sm text-gray-500">{file.name}</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {file ? (
              <Button
                onClick={onRemove}
                className="text-red-500 border-red-500 hover:bg-red-50"
                size="small"
                icon={<DeleteOutlined />}
              >
                {translate("CREATE_REQUEST.REMOVE")}
              </Button>
            ) : (
              <Upload {...uploadProps}>
                <Button
                  type="primary"
                  className="bg-primary border-primary hover:bg-primary-dark"
                >
                  {translate("CREATE_REQUEST.SELECT_FILE")}
                </Button>
              </Upload>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ── Header card — vendor info (mirrors legacy schoolCards layout) ──────────
  const renderVendorCard = () => (
    <div className="w-full max-w-[530px] max-h[250px] overflow-hidden">
      <div className="bg-[#E6F1F2] rounded-lg overflow-hidden flex flex-row bg-[linear-gradient(to_left,#E1DBDB,#F9F9F9)]">
        <div className="md:w-2/5 sm:w-2/5 w-1/2">
          <div className="flex justify-center items-center h-full w-full">
            <img
              src={
                !vendor?.LOGO_URL || logoFailed ? vendorPlaceholder : vendor.LOGO_URL
              }
              alt={vendorName}
              className="w-[80%] max-h-full object-contain"
              onError={() => setLogoFailed(true)}
            />
          </div>
        </div>
        <div className="p-6 flex flex-col justify-between flex-1">
          <div>
            <h3 className="text-[#1F225B] text-xl md:text-2xl font-light mb-2">
              {vendorName}
            </h3>
            <p className="text-[#8E9196] text-sm md:text-base mb-4">
              {(isArabic ? vendor?.SHORT_DESC_AR : vendor?.SHORT_DESC_EN) ||
                ""}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  // ── Detailed description + terms list (mirrors legacy schoolCardDetails) ───
  const renderVendorDetails = () => (
    <div
      className="bg-transparent shadow-none rounded-none border-none outline-none"
      style={{
        width: "100%",
        maxWidth: "550px",
        boxShadow: "none",
        border: "none",
        borderRadius: "0",
        outline: "none",
        padding: "0",
      }}
    >
      <div className="my-5">
        <p className="text-[#878787] mt-3 text-base md:text-lg font-light text-start mb-2">
          {vendorLongDesc}
        </p>
      </div>

      {/* Vendor terms list — replaces the hardcoded EDUCATIONAL.AUTO_PT* list from legacy */}
      {vendorTerms.length > 0 && (
        <ul className="mt-5">
          {vendorTerms.map((text, index) => (
            <li className="flex mb-4 gap-2" key={index}>
              <span className="flex-shrink-0">
                <img src={Arrow} alt="checkmark" className="w-6 h-6" />
              </span>
              <span className="text-sm break-words">{text}</span>
            </li>
          ))}
        </ul>
      )}

      {renderRequestedAmountNotice("mt-6 hidden md:block")}
    </div>
  );

  // Notice card kept identical to legacy — the dynamic min/max now lives in the
  // Yup error itself ("Must be at least <min>" / "Cannot exceed <max>").
  const renderRequestedAmountNotice = (className = "") => (
    <div className={`bg-[#F4FAFB] p-5 ${className}`.trim()}>
      <p className="mb-2 text-start text-base font-medium text-[#2F2F2F] md:text-base">
        {translate("PRODUCTS.REQUESTED_AMOUNT_NOTICE_TITLE")}
      </p>
      <p className="text-start text-sm font-normal leading-6 text-[#2F2F2F] md:text-base">
        {translate("PRODUCTS.REQUESTED_AMOUNT_NOTICE_TEXT")}
      </p>
    </div>
  );

  // ── Full-page loader while vendor resolves ─────────────────────────────────
  if (vendorLoading || !vendor) {
    return (
      <div className="rounded-xl mb-4 px-4 py-4 bg-white shadow-lg">
        <div className="p-5 flex flex-col items-center justify-center min-h-[400px] gap-4">
          <LoadingOutlined style={{ fontSize: 32, color: "#1F225B" }} />
          <p className="text-[#5F5F5F]">{translate("Loading...") || "Loading…"}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl mb-4 px-4 py-4 bg-white shadow-lg">
      <SalaryEligibilityGate productCode="MUR" />
      <div className="p-5">
        <BackButton onClick={() => navigate(-1)} />

        <Row gutter={[16, 16]} className="pt-4">
          {/* Vendor card + details */}
          <Col xs={24} md={10}>
            {renderVendorCard()}
            {renderVendorDetails()}
          </Col>

          {/* Form */}
          <Col xs={24} md={14}>
            <Formik
              innerRef={formikRef}
              enableReinitialize
              validateOnChange={true}
              validateOnMount={true}
              initialValues={buildInitialValues(schemaFields)}
              validationSchema={buildValidationSchema(schemaFields)}
              onSubmit={(values) => schoolAppCreateV2(values)}
            >
              {(props) => (
                <Form onFinish={() => props.handleSubmit()}>
                  <Title level={5} style={{ color: "#030438" }}>
                    {translate("VOUCHERS.FILL_FORM")}
                  </Title>
                  <div>
                    <FormDrawer schema={{ fields: schemaFields }} />
                  </div>
                  <div className="my-4">
                    <label className="block text-gray-600 font-medium mb-2">
                      {translate("SETTINGS.DOCUMENT_DES_V2")}
                    </label>

                    <V2DocumentSelectionComponent
                      title="BANK_STATEMENT"
                      file={selectedBankStatement}
                      onSelect={setSelectedBankStatement}
                      onRemove={() => setSelectedBankStatement(null)}
                    />

                    <V2DocumentSelectionComponent
                      title="PAY_SLIP"
                      file={selectedPaySlip}
                      onSelect={setSelectedPaySlip}
                      onRemove={() => setSelectedPaySlip(null)}
                    />

                    <V2DocumentSelectionComponent
                      title="OTHER_DOCUMENTS"
                      file={selectedOtherDocuments}
                      onSelect={setSelectedOtherDocuments}
                      onRemove={() => setSelectedOtherDocuments(null)}
                    />
                  </div>

                  {renderRequestedAmountNotice("mt-6 md:hidden")}

                  <div className="flex justify-end mt-8">
                    <Button
                      disabled={
                        !props.isValid ||
                        isCreating ||
                        isLoading ||
                        isAnyFileUploading ||
                        isUploading
                      }
                      type="primary"
                      htmlType="submit"
                      className="mt-4 bg-primary border-none text-white font-semibold p-6 w-40"
                    >
                      {isLoading ||
                      isAnyFileUploading ||
                      isCreating ||
                      isUploading ? (
                        <LoadingOutlined />
                      ) : (
                        translate("APPLY")
                      )}
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </Col>
        </Row>
      </div>

      {/* IVR verification modal — identical UX to legacy SchoolProducts */}
      <Modal
        open={isIvrModalVisible}
        footer={null}
        closable={true}
        closeIcon={<CloseOutlined style={{ fontSize: "18px", color: "#000" }} />}
        onCancel={() => {
          setIsIvrModalVisible(false);
          if (ivrApplicationId) {
            navigate(
              `/${routes.APP_ROOT}/${routes.CREATE_REQUEST}/${routes.CREATE_REQUEST_SUCCESS}/${ivrApplicationId}/2`,
              { replace: true }
            );
          }
        }}
        centered
        width={500}
        bodyStyle={{ padding: "40px" }}
      >
        <div className="flex flex-col items-center text-center">
          <img
            src={ivrPending}
            alt="IVR Verification"
            className="w-64 h-64 mb-6"
          />
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">
            {translate("CREATE_REQUEST.IVR_VERIFICATION_TITLE")}
          </h2>
          <p className="text-gray-600 text-base leading-relaxed">
            {translate("CREATE_REQUEST.IVR_VERIFICATION_MESSAGE")}
          </p>
          <Button
            type="primary"
            className="mt-6 bg-primary border-none text-white font-semibold px-16 py-5 rounded-3xl hover:bg-secondary"
            onClick={() => {
              setIsIvrModalVisible(false);
              if (ivrApplicationId) {
                navigate(
                  `/${routes.APP_ROOT}/${routes.CREATE_REQUEST}/${routes.CREATE_REQUEST_SUCCESS}/${ivrApplicationId}/2`,
                  { replace: true }
                );
              }
            }}
          >
            {translate("CREATE_REQUEST.CONTINUE")}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default SchoolProductsV2;
